// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { parsePeriod } from './utils';
import { useDashboard } from '@Hooks/useDashboard';
import useStorageSearch from '@Hooks/useStorageSearch';
import { SearchScopeType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import { useListAllFields } from '@Queries/describeQueries';
import { classList } from '@Helpers/className';
import _ from 'lodash';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useLocalMessage } from '@Helpers/locale';
import { ActionWidget } from './ActionWidget';
import * as htmlToImage from 'html-to-image';
import { downloadFile } from '@Helpers/download';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { baseUrl } from '../../../index';
import { ChartOutput, CounterWidgetType, StatsAggType, WidgetOutput, WidgetSeriesOutput } from 'thehive-sdk';
import { UseQueryResult } from 'react-query';

import './CounterWidget.scss';

interface CounterChartOutput {
    name?: string;
    value: number;
    agg: StatsAggType;
    threshold?: number;
    orientation?: 'min' | 'max';
    [key: string]: any;
}

const prepareChartData = (counterData: ChartOutput, series: WidgetSeriesOutput[]): CounterChartOutput[] => {
    return Object.entries(counterData).map(([, value], index) => ({
        name: series[index].name || series[index].label,
        value,
        agg: series[index].agg,
        threshold: series[index].threshold,
        orientation: series[index].orientation || 'min',
    }));
};

interface CounterWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput>;
}

export const CounterWidget = ({ widget, query }: CounterWidgetProps): ReactElement => {
    const i = useLocalMessage();

    const options = widget.options as CounterWidgetType;

    const { data: describeData } = useListAllFields();
    const { setSearchActiveScope, setSearchFilters, setSearchKeyword } = useStorageSearch();
    const { entity, series = [], filters = [], periodDateField = '' } = options;
    const { dashboard } = useDashboard();
    const {
        definition: { period, customPeriod },
    } = dashboard;
    const { from, to } = parsePeriod(customPeriod, period);

    const periodFilters =
        period !== 'all'
            ? [
                  {
                      field: periodDateField,
                      type: 'date',
                      operator: 'custom',
                      values: [from, to],
                      describeAttribute: describeData?.[entity]?.attributes.find(
                          ({ name }) => name === periodDateField,
                      ) as any,
                  },
              ]
            : [];

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    if (!query?.data || _.isEmpty(query?.data)) {
        return (
            <div className="widget__empty">
                <DisplayEmptyData label={i('dashboard.widget.empty')} />
            </div>
        );
    }

    const dataTreated = prepareChartData(query?.data, series);

    return (
        <div className="widget__display">
            <div
                className="counter-widget"
                id={`counter-widget-${widget.id}`}
                style={{
                    gridTemplateColumns: `repeat(${(dataTreated?.length % 3) + 1}, 1fr)`,
                    padding: dataTreated.length === 1 ? '4rem' : undefined,
                }}
            >
                {dataTreated.map(({ name, value, agg, threshold, orientation }, index) => (
                    <div
                        key={index}
                        className={classList('counter-widget__box', {
                            positive:
                                _.isNumber(threshold) &&
                                (orientation === 'min' ? value >= threshold : value <= threshold),
                            negative:
                                _.isNumber(threshold) &&
                                (orientation === 'max' ? value > threshold : value < threshold),
                        })}
                    >
                        <div
                            className={classList('counter-widget__value', { clickable: agg === 'count' })}
                            onClick={() => {
                                if (agg === 'count') {
                                    const serie = series[index];

                                    setSearchFilters(entity as SearchScopeType, [
                                        ...periodFilters,
                                        ...serie.filters,
                                        ...filters,
                                    ]);
                                    setSearchActiveScope(entity as SearchScopeType);
                                    setSearchKeyword(entity as SearchScopeType, '');
                                    setTimeout(() => {
                                        window.open(
                                            baseUrl.substring(0, baseUrl.length - 1) +
                                                `${RouteBuilder.compute('search.default')}?fetch=true`,
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                    }, 200);
                                }
                            }}
                        >
                            {Math.round((value + Number.EPSILON) * 100) / 100}
                        </div>
                        {name && <div className="counter-widget__name">{name}</div>}
                    </div>
                ))}
            </div>

            <ActionWidget
                onDownloadImage={() => {
                    const element = document.getElementById(`counter-widget-${widget.id}`);

                    if (!element) {
                        return;
                    }

                    htmlToImage
                        .toPng(element, { backgroundColor: 'transparent' })
                        .then((file) => downloadFile(widget.options.title || 'widget', file, 'image/png', true));
                }}
                onDownloadCSV={() => {
                    const header = 'field,count';
                    const body = dataTreated.map(({ value, name }) => `${name},${value}`).join('\n');
                    const tab = header + '\n' + body;

                    downloadFile(`${widget.options?.title || ''}.csv`, tab, 'text/csv');
                }}
            />
        </div>
    );
};
